import React, { useEffect, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { formatPhone } from '../../voice/callFormat';
import { missedCallsApi, errorMessage } from './missedCallsApi';
import { formatDateTime, smsLengthText, whoLines } from './missedCallFormat';
import { SMS_TEMPLATES } from './smsTemplates';

/**
 * Text the row's number that we tried and will call again (docs/MISSED_CALLS_PLAN.md §5.5): opens on the office's
 * 1차, 2차 template, with a button per template (smsTemplates.js), in an editable field, sent from the Twilio SMS
 * number, which says 발신 전용. Outside production the server sends nothing, or only to a test number.
 */
const SmsDialog = ({ row, open, onClose, defaultText = SMS_TEMPLATES[0].text, maxLength = 500, onSent }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [text, setText] = useState(defaultText);
    const [sending, setSending] = useState(false);

    useEffect(() => { if (open) setText(defaultText); }, [open, defaultText]);

    const send = async () => {
        setSending(true);
        try {
            const res = await missedCallsApi.sendSms(row.id, text);
            onSent(res);
            onClose();
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
            if (err?.response?.data?.row) onSent({ row: err.response.data.row }); // the failure is in the history
        } finally {
            setSending(false);
        }
    };

    if (!row) return null;
    const counter = smsLengthText(text, maxLength);
    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="sm" disableEnforceFocus>
            <DialogTitle>문자 보내기</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2"><b>받는 사람</b> {formatPhone(row.phone_number)} · {whoLines(row).map((l) => l.text).join(', ')}</Typography>
                    <Typography variant="body2"><b>보내는 번호</b> A ONE 문자 번호 (발신 전용)</Typography>
                    {row.last_sms_at && (
                        <Typography variant="caption" color="warning.main">마지막 문자: {formatDateTime(row.last_sms_at)} ({row.sms_count}회)</Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {SMS_TEMPLATES.map((t) => (
                        <Button key={t.key} size="small" variant="outlined" onClick={() => setText(t.text.slice(0, maxLength))} disabled={sending}>
                            {t.label}
                        </Button>
                    ))}
                </Box>
                <TextField
                    value={text}
                    onChange={(e) => setText(e.target.value.slice(0, maxLength))}
                    multiline
                    minRows={4}
                    fullWidth
                    autoFocus
                    helperText={counter.cost ? <>{counter.count}<br />{counter.cost}</> : counter.count}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>취소</Button>
                <Button variant="contained" onClick={send} disabled={sending || !text.trim()}>
                    {sending ? '보내는 중…' : '보내기'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SmsDialog;
